/**
 * TestRunner — decides whether a test should render as a Reading Challenge
 * (sticky passage + stacked question cards) or as the standard TestTaking UI.
 *
 * It loads the test session once, then passes it as `initialSession` to
 * whichever component is rendered, so neither child needs to re-fetch.
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import TestTaking from './TestTaking';
import ReadingChallenge from './ReadingChallenge';
import FillInTheBlank from './FillInTheBlank';
import WritingTask from './WritingTask';
import PaywallUpsellCard from './billing/PaywallUpsellCard';
import { getPaywallError, PaywallError } from '../api/billingErrors';

// ─────────────────────────────────────────────
// Types (minimal; mirrors TestTaking.tsx)
// ─────────────────────────────────────────────

interface PassageShape {
  title?: string;
  content?: string | { text?: string } | null;
}

interface Question {
  id: string;
  subject?: string;
  content?: {
    passage?: PassageShape;
    stimulus?: string | { text?: string } | null;
    responseFormat?: string;
    blankIndex?: number;
    passageId?: string;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

interface TestSession {
  sessionId: string;
  test: { id: string; title: string; [key: string]: any };
  questions: Question[];
  timeLimit: number;
}

// ─────────────────────────────────────────────
// Props — same surface as TestTaking
// ─────────────────────────────────────────────

export interface TestRunnerProps {
  testId: string;
  onComplete: (result: any) => void;
  onCancel: () => void;
}

// ─────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────

const richTextToString = (v: string | { text?: string } | null | undefined): string => {
  if (!v) return '';
  if (typeof v === 'string') return v;
  return v.text || '';
};

/** Returns true when questions are fill-in-the-blank (cloze) type. */
const isFillInSet = (questions: Question[]): boolean => {
  if (questions.length === 0) return false;
  return questions.some(
    q =>
      q?.content?.responseFormat === 'text' &&
      q?.content?.blankIndex !== undefined &&
      q?.content?.passageId
  );
};

/** Returns true when the test is an extended-response writing task. */
const isWritingSet = (questions: Question[]): boolean => {
  if (questions.length === 0) return false;
  return questions.some(
    q => q?.subject === 'writing' || q?.content?.responseFormat === 'essay'
  );
};

/** Returns true when at least one question carries a non-empty passage. */
const isReadingSet = (questions: Question[]): boolean => {
  if (questions.length === 0) return false;
  return questions.some(q => {
    const p = q?.content?.passage;
    if (!p) return false;
    return !!(p.title || richTextToString(p.content).trim());
  });
};

// ─────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────

const TestRunner: React.FC<TestRunnerProps> = ({ testId, onComplete, onCancel }) => {
  const { user: authUser } = useAuth();
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

  const getCurrentUser = () => {
    if (authUser) return authUser;
    try {
      const s = localStorage.getItem('cleveroo_user');
      if (s) return JSON.parse(s);
    } catch (e) {}
    return null;
  };
  const currentUser = getCurrentUser();

  const [session, setSession] = useState<TestSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [paywallError, setPaywallError] = useState<PaywallError | null>(null);

  useEffect(() => {
    if (!currentUser?.id) {
      setLoadError('User not authenticated');
      setLoading(false);
      return;
    }
    setLoading(true);
    setPaywallError(null);
    axios
      .get(`${API_BASE_URL}/api/students/${currentUser.id}/tests/${testId}/start`, {
        headers: { 'Content-Type': 'application/json' },
        withCredentials: false,
      })
      .then(res => {
        if (res.data.success) {
          setSession(res.data.data);
        } else {
          setLoadError(res.data.error || 'Failed to start test');
        }
      })
      .catch(err => {
        const paywall = getPaywallError(err);
        if (paywall) {
          setPaywallError(paywall);
          return;
        }
        setLoadError(err.response?.data?.error || err.message || 'Failed to start test');
      })
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testId]);

  // ── Loading state ──
  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          flexDirection: 'column',
          gap: 16,
          fontFamily: 'Lexend, system-ui, sans-serif',
        }}
      >
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            border: '3px solid #EFE6D0',
            borderTopColor: '#14593C',
            animation: 'spin 0.8s linear infinite',
          }}
        />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        <span style={{ fontSize: 15, fontWeight: 600, color: '#6B7A6F' }}>Loading test…</span>
      </div>
    );
  }

  // ── Paywall state (free tests used up / premium-only test) ──
  if (paywallError) {
    return <PaywallUpsellCard paywallError={paywallError} onBack={onCancel} />;
  }

  // ── Error state ──
  if (loadError || !session) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          flexDirection: 'column',
          gap: 16,
          padding: 24,
          textAlign: 'center',
        }}
      >
        <h2 style={{ color: '#E26B4F', margin: 0 }}>Couldn't load the test</h2>
        <p style={{ color: '#6B7A6F', fontSize: 15, margin: 0 }}>{loadError || 'Unknown error'}</p>
        <button
          style={{
            padding: '10px 22px',
            borderRadius: 999,
            border: '1.5px solid #E5D9BD',
            background: '#fff',
            fontWeight: 700,
            fontSize: 14,
            cursor: 'pointer',
            color: '#14593C',
          }}
          onClick={onCancel}
        >
          Back to dashboard
        </button>
      </div>
    );
  }

  // ── Route to the right component ──
  // Check fill-in first (cloze), then writing (essay), then reading, then generic.
  const fillIn = isFillInSet(session.questions);
  const writing = !fillIn && isWritingSet(session.questions);
  const reading = !fillIn && !writing && isReadingSet(session.questions);

  if (fillIn) {
    return (
      <FillInTheBlank
        testId={testId}
        initialSession={session}
        onComplete={onComplete}
        onCancel={onCancel}
      />
    );
  }

  if (writing) {
    return (
      <WritingTask
        testId={testId}
        initialSession={session}
        onComplete={onComplete}
        onCancel={onCancel}
      />
    );
  }

  if (reading) {
    return (
      <ReadingChallenge
        testId={testId}
        initialSession={session}
        onComplete={onComplete}
        onCancel={onCancel}
      />
    );
  }

  return (
    <TestTaking
      testId={testId}
      initialSession={session}
      onComplete={onComplete}
      onCancel={onCancel}
    />
  );
};

export default TestRunner;
